/**
 * Async Select API Client
 * Shared utilities for fetching options and values for async select components
 */

import { buildSimpleFilterQuery } from './advancedSearchQuery';

export interface SelectOption {
  value: string;
  label: string;
  data?: Record<string, unknown>;
}

export interface AsyncSelectApiOptions {
  apiEndpoint: string;
  searchField?: string; // Optional - not needed for ID-based fetches
  valueField: string;
  labelField: string;
}

export interface FetchOptionsResult {
  options: SelectOption[];
  total: number;
  hasMore: boolean;
}

/**
 * Fetch options for async select with pagination and search
 *
 * @param options - Configuration for API call
 * @param searchTerm - Optional search term to filter results
 * @param limit - Number of items per page (default: 20)
 * @param offset - Starting offset for pagination (default: 0)
 * @returns Promise with options array, total count, and hasMore flag
 *
 * @example
 * const result = await fetchAsyncSelectOptions({
 *   apiEndpoint: '/grades',
 *   searchField: 'grade_name',
 *   valueField: 'grade_id',
 *   labelField: 'grade_name'
 * }, 'GMP', 20, 0);
 */
export async function fetchAsyncSelectOptions(
  options: AsyncSelectApiOptions,
  searchTerm?: string,
  limit: number = 20,
  offset: number = 0
): Promise<FetchOptionsResult> {
  const { apiEndpoint, searchField, valueField, labelField } = options;

  // Validation
  if (!apiEndpoint) {
    console.error('fetchAsyncSelectOptions: apiEndpoint is required');
    return { options: [], total: 0, hasMore: false };
  }

  if (!valueField || !labelField) {
    console.error(
      'fetchAsyncSelectOptions: valueField and labelField are required'
    );
    return { options: [], total: 0, hasMore: false };
  }

  try {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    // Add advanced search if search term is provided
    if (searchTerm && searchField) {
      const advancedSearch = buildSimpleFilterQuery(
        searchField,
        'contains',
        searchTerm
      );
      params.append('advancedSearch', JSON.stringify(advancedSearch));
    }

    const url = `/api${apiEndpoint}?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      const errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      console.error(errorMessage, { apiEndpoint, searchTerm });
      throw new Error(errorMessage);
    }

    const data = await response.json();

    // Validate response structure
    if (!data || typeof data !== 'object') {
      console.error('Invalid API response format', { apiEndpoint, data });
      return { options: [], total: 0, hasMore: false };
    }

    // Extract items array - handle different response formats
    const items = data.items || data.data || data[Object.keys(data)[0]] || [];
    const total = data.total || items.length;

    // Validate items is an array
    if (!Array.isArray(items)) {
      console.error('API response items is not an array', {
        apiEndpoint,
        items,
      });
      return { options: [], total: 0, hasMore: false };
    }

    // Convert to select options with validation
    const selectOptions: SelectOption[] = items
      .map((item: Record<string, unknown>) => {
        const value = item[valueField];
        const label = item[labelField];

        // Skip items with missing required fields
        if (value === undefined || value === null) {
          console.warn(`Item missing valueField '${valueField}'`, item);
          return null;
        }

        return {
          value: String(value),
          label: String(label) || 'Unnamed',
          data: item,
        } as SelectOption;
      })
      .filter((option): option is SelectOption => option !== null);

    const hasMore = offset + items.length < total;

    return {
      options: selectOptions,
      total,
      hasMore,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('Error fetching async select options:', {
      error: errorMessage,
      apiEndpoint,
      searchTerm,
    });
    throw error;
  }
}

/**
 * Fetch a single value by ID for displaying selected option
 * Handles the case where backend doesn't support searching by ID field
 *
 * @param options - Configuration for API call
 * @param id - The ID value to fetch
 * @returns Promise with SelectOption or null if not found
 *
 * @example
 * const option = await fetchAsyncSelectValueById({
 *   apiEndpoint: '/grades',
 *   searchField: 'grade_name',
 *   valueField: 'grade_id',
 *   labelField: 'grade_name'
 * }, '4');
 */
export async function fetchAsyncSelectValueById(
  options: AsyncSelectApiOptions,
  id: string
): Promise<SelectOption | null> {
  if (!id) return null;

  const { apiEndpoint, valueField, labelField } = options;

  try {
    const params = new URLSearchParams({
      limit: '1',
      offset: '0',
    });

    // Fetch by exact match on value field (which is the ID field in modal context)
    const advancedSearch = buildSimpleFilterQuery(valueField, 'equals', id);
    params.append('advancedSearch', JSON.stringify(advancedSearch));

    const url = `/api${apiEndpoint}?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      // If API call fails, just show the value as the label
      return { value: id, label: id };
    }

    const data = await response.json();
    const items = data.items || data.data || data[Object.keys(data)[0]] || [];

    if (items.length > 0) {
      const item = items[0];
      return {
        value: String(item[valueField]),
        label: item[labelField] || 'Unnamed',
        data: item,
      };
    }

    // If no items found, show the value as the label
    return { value: id, label: id };
  } catch (error) {
    console.error('Error fetching value by ID:', error);
    // On error, just show the value as the label
    return { value: id, label: id };
  }
}

/**
 * Fetch multiple values by IDs for displaying selected options
 * Useful for multi-select components
 *
 * @param options - Configuration for API call
 * @param ids - Array of ID values to fetch
 * @returns Promise with array of SelectOptions
 */
export async function fetchAsyncSelectValuesByIds(
  options: AsyncSelectApiOptions,
  ids: string[]
): Promise<SelectOption[]> {
  if (!ids || ids.length === 0) return [];

  // Fetch all IDs concurrently
  const promises = ids.map(id => fetchAsyncSelectValueById(options, id));
  const results = await Promise.all(promises);

  // Filter out null results
  return results.filter((option): option is SelectOption => option !== null);
}
